import { ApolloClient, DocumentNode } from '@apollo/client'
import { PageInfo } from '../../../../core/models'

export interface PaginationResult<T> {
  items: T[]
  pageInfo: PageInfo
}

/**
 * Generic mechanism to fetch all pages from a GraphQL query.
 * Fetches in chunks until hasNextPage is false.
 */
export const fetchAllPages = async <T, V>(
  client: ApolloClient<any>,
  query: DocumentNode,
  variables: V,
  extractData: (data: any) => PaginationResult<T>
): Promise<T[]> => {
  let allItems: T[] = []
  let hasNext = true
  let currentSkip = (variables as any).skip || 0
  const take = (variables as any).take || 100

  try {
    while (hasNext) {
      const { data } = await client.query({
        query,
        variables: { ...variables, skip: currentSkip, take },
        fetchPolicy: 'network-only',
      })

      const result = extractData(data)
      if (!result) break

      const { items, pageInfo } = result
      allItems = [...allItems, ...items]
      
      // Stop if no more pages or no items returned (safety)
      hasNext = pageInfo?.hasNextPage && items.length > 0
      currentSkip += items.length

      if (!hasNext) break
    }
  } catch (error) {
    console.error('Error in fetchAllPages:', error)
    throw error
  }

  return allItems
}
